import { Button } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  applyEdgeChanges,
  applyNodeChanges,
  DefaultEdgeOptions,
  Edge,
  EdgeChange,
  MarkerType,
  Node,
  NodeMouseHandler,
  NodeTypes,
  OnConnect,
  OnNodesChange,
  Panel,
  Position,
  ReactFlowInstance,
  ReactFlowProvider,
} from 'reactflow';

import 'reactflow/dist/style.css';
import { DownloadIconButton, IconButton } from '../IconButton';
import { FileUploadButton } from './FileUploadButton';
import { repositionGraph } from './layout/d3hierarchy';
import { GraphPositions } from './layout/layoutTypes';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';

export type { NodeTypes };

// every reactFlow node has an id, type, data, and position
// {
//   // identifier for the node
//   id: string;
//
//   // type of the node. this is generic
//   // you would typically use 'artifactType' for nodeType.
//   type: U;

//   // node data can be generic, but
//   // instead of letting reactFlow manage our node data
//   // we let it only store the nodeIds, and manage the data
//   // ourselfs. hence our data is always string.
//   data: string;
//
//   // we will let reactFlow manage the position of the node.
//   // and only supply it with store for the position.
//   position: XYPosition;
// }

export type InternalNode<U extends string> = Node<string, U>;

export type ExternalNode<U extends string> = {
  id: string;
  type: U;
};

export type ReactFlowWrapperEdge = Edge;

interface ReactFlowWrapperControlProps<T extends string> {
  topRight?: React.ReactNode;
  topLeft?: React.ReactNode;
  elementRef: React.RefObject<HTMLDivElement>;
  nodeTypes: NodeTypes;
  initialNodes: ExternalNode<T>[];
  initialEdges: ReactFlowWrapperEdge[];
  onNodeClicked: (node: ExternalNode<T> | null) => void;

  // suppply this callback if you want to allow edge addition/deletion.
  onEdgeAdded?: (params: { source: string; target: string }) => void;
  onEdgeDeleted?: (params: { source: string; target: string }) => void;

  // supply this call back if you want to drop new nodes on the graph
  // this should return a new node id.
  onNewNode?: (nodeType: T) => string | null;

  // handle importing graph.
  handleImport?: (file: File) => Promise<GraphPositions>;

  handleExport?: (positions: GraphPositions) => void;

  xSpacing?: number;
  ySpacing?: number;
  disconnectedSpacing?: number;
}

const defaultViewport = { x: 0, y: 0, zoom: 2 }; // start somewhat zoomed out.

const nodeDefaults = {
  sourcePosition: Position.Right,
  targetPosition: Position.Left,
  position: { x: 100, y: 100 },
};

const edgeColor = '#B5B5B5';
const defaultEdgeOptions: DefaultEdgeOptions = {
  animated: true,
  style: { stroke: edgeColor, strokeWidth: 2 }, // Adjust strokeWidth and stroke color here

  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: edgeColor,
  },
};

// this encapsulates react flow wrapper.
// it is a controlled component, so it needs to be passed the initial nodes and edges
export const ReactFlowWrapperControl = <T extends string>({
  topLeft,
  topRight,
  elementRef,
  nodeTypes,
  initialNodes,
  initialEdges,
  onNodeClicked,
  onEdgeAdded,
  onEdgeDeleted,
  onNewNode,
  handleExport,
  handleImport,
  xSpacing = 300,
  ySpacing = 300,
  disconnectedSpacing = 400,
}: ReactFlowWrapperControlProps<T>) => {
  const [positions, setPositions] = useState<GraphPositions>({});
  const flowNodesToPositions = useCallback(
    (flowNodes: InternalNode<T>[]): GraphPositions => {
      return flowNodes.reduce((acc, node) => {
        return {
          ...acc,
          [node.id]: node.position,
        };
      }, {} as GraphPositions);
    },
    [],
  );

  // triggered by ReactFlowWrapperControl
  // when nodes move around or a connection is made.
  // we extract new position information and pass it to the parent
  const onGraphChanged = useCallback(
    (updatedNodes: InternalNode<T>[], _edges: ReactFlowWrapperEdge[]) => {
      setPositions(flowNodesToPositions(updatedNodes));
    },
    [flowNodesToPositions, setPositions],
  );

  const [edges, setEdges] = useState<Edge[]>(initialEdges);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<
    string,
    string
  > | null>(null);
  useEffect(() => {
    setEdges(initialEdges);
    setSelectedEdge(null);
  }, [initialEdges]);

  // when rendering the graph first time
  // we wont have positions in our state.
  // we need to repostion.
  const needsReposition = initialNodes.some((node) => !positions[node.id]);
  const newPositions = needsReposition
    ? repositionGraph({
        nodes: initialNodes.map((node) => node.id),
        edges: initialEdges,
        xSpacing,
        ySpacing,
        disconnectedSpacing,
      })
    : positions;
  const nodes = initialNodes.map((node) => {
    const position = newPositions[node.id];
    return {
      ...nodeDefaults,
      ...node,
      data: node.id,
      position,
    };
  });
  if (needsReposition) {
    // let parent update the state of the positions.
    setPositions(newPositions);
  }

  const onRepositionGraph = useCallback(() => {
    const updatedPositions = repositionGraph({
      nodes: nodes.map(({ id }) => id),
      edges,
      xSpacing,
      ySpacing,
      disconnectedSpacing,
    });
    setPositions(updatedPositions);
  }, [nodes, edges, setPositions, xSpacing, ySpacing, disconnectedSpacing]);

  const onNodesChange = useCallback<OnNodesChange>(
    (changes) => {
      const updatedNodes = applyNodeChanges(changes, nodes);
      onGraphChanged(updatedNodes as InternalNode<T>[], edges);
    },
    [edges, onGraphChanged, nodes],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // changes in selection need not be notified to the parent
      const selectionChangesOnly = changes.every(
        (change) => change.type === 'select',
      );

      const updatedEdges = applyEdgeChanges(changes, edges);
      setSelectedEdge(null);
      if (selectionChangesOnly) {
        // if only single edge was selected, then set it as selected edge
        if (changes.length === 1) {
          const change = changes[0];
          if (change.type === 'select' && change.selected) {
            const newSelectedEdge = edges.find((edge) => edge.id === change.id);
            setSelectedEdge(newSelectedEdge || null);
          }
        }
        setEdges(updatedEdges);
      } else {
        onGraphChanged(nodes, updatedEdges);
      }
    },
    [edges, onGraphChanged, nodes],
  );

  const onConnect = useCallback<OnConnect>(
    (connection) => {
      const { source, target } = connection;
      if (onEdgeAdded) {
        // @ts-ignore !TODO @eldarshamukhamedov Either onEdgeAdded is mis-typed, or we're missing a null guard around `source` and `target`
        onEdgeAdded({ source, target });
      }
    },
    [onEdgeAdded],
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (onNewNode) {
        event.preventDefault();
        if (elementRef.current && reactFlowInstance) {
          const reactFlowBounds = elementRef.current.getBoundingClientRect();
          const type = event.dataTransfer.getData('application/reactflow');
          const position = reactFlowInstance.project({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
          });
          // @ts-ignore !TODO @eldarshamukhamedov Looks like onNewNode is mis-typed
          const newNodeId = onNewNode(type);
          if (newNodeId) {
            setPositions({
              ...positions,
              [newNodeId]: position,
            });
          }
        }
      }
    },
    [elementRef, onNewNode, positions, reactFlowInstance],
  );

  const nodeClicked = useCallback<NodeMouseHandler>(
    (_event, node) => {
      // @ts-ignore !TODO @eldarshamukhamedov Either onNodeClicked is mis-typed, or we're missing some transformation logic here
      onNodeClicked(node);
    },
    [onNodeClicked],
  );

  return (
    <ReactFlowProvider>
      <ReactFlow
        disableKeyboardA11y
        nodeTypes={nodeTypes}
        nodes={nodes}
        edges={edges}
        onInit={setReactFlowInstance}
        elevateEdgesOnSelect
        defaultEdgeOptions={defaultEdgeOptions}
        onNodeClick={nodeClicked}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDragOver={onDragOver}
        onDrop={onDrop}
        defaultViewport={defaultViewport}
        fitView
        minZoom={0.1}
        maxZoom={50}
        proOptions={{
          hideAttribution: true,
        }}
      >
        {topLeft && <Panel position="top-left">{topLeft}</Panel>}
        {topRight && <Panel position="top-right">{topRight}</Panel>}
        {selectedEdge && onEdgeDeleted && (
          <Panel position="bottom-left">
            <Button
              onClick={() => {
                const { source, target } = selectedEdge;
                onEdgeDeleted({ source, target });
              }}
            >
              Delete Edge
            </Button>
          </Panel>
        )}
        <Panel position="bottom-right">
          {handleExport && (
            <DownloadIconButton
              onClick={() => handleExport(positions)}
              ariaLabel="Export"
              label="Export"
            >
              Export
            </DownloadIconButton>
          )}
          {handleImport && (
            <FileUploadButton
              allowedExtensions={['json']}
              buttonText="Import"
              modalHeaderText="Import Graph"
              handleFile={async (file: File) => {
                const newPos = await handleImport(file);
                setPositions(newPos);
              }}
            >
              Export
            </FileUploadButton>
          )}

          <IconButton
            ariaLabel="Reposition"
            label="Reposition"
            icon={<FaIcon icon="arrows-rotate-reverse" />}
            onClick={() => onRepositionGraph()}
          >
            Reposition
          </IconButton>
        </Panel>
      </ReactFlow>
    </ReactFlowProvider>
  );
};
